import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logoLight from '../../assets/brand/logo-light.png';

// Admin-only header, kept separate from the shared AppHeader (used by
// QuizPortal) rather than overloading it with admin-specific concerns
// (Profile/Settings dropdown, always-light theme) - user asked specifically
// for the admin console to default to light (matching the home page) and
// gain a Profile/Settings/Logout menu, 2026-08-07 (docs/DECISIONS.md).
export default function AdminHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    if (!window.confirm('Do you really want to logout?')) return;
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 px-4 sm:px-6 py-4">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-4 min-w-0">
          <img src={logoLight} alt="RUDVAN" className="h-10 sm:h-11 w-auto flex-shrink-0" />
        </div>

        {user && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex items-center gap-2 pl-2 pr-3 py-1.5 bg-surface-light border border-gray-200 rounded-btn hover:bg-gray-100 transition-colors"
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-brand-blue/15 flex items-center justify-center">
                  <User size={14} className="text-brand-blue" />
                </div>
              )}
              <span className="text-sm font-medium text-navy hidden sm:inline">
                {user.name?.split(' ')[0] || user.email}
              </span>
              <ChevronDown size={14} className={`text-text-slate transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-card border border-gray-200 bg-white shadow-brand-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-navy truncate">{user.name || 'Admin'}</p>
                  <p className="text-xs text-text-slate truncate">{user.email}</p>
                </div>
                <button
                  onClick={() => { setMenuOpen(false); navigate('/admin/profile'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-surface-light transition-colors text-left"
                >
                  <User size={16} className="text-text-slate" />
                  Profile
                </button>
                <button
                  onClick={() => { setMenuOpen(false); navigate('/admin/settings'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-surface-light transition-colors text-left"
                >
                  <Settings size={16} className="text-text-slate" />
                  Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left border-t border-gray-100"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
